import { FC, useMemo, useRef, useState } from 'react'
import { styled } from '@mui/material/styles'
import Button, { ButtonProps } from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'

interface CityFilterProps {
  /** Список городов для выбора */
  cities: string[]
  /** Текущий выбранный город или null («все») */
  selectedCity: string | null
  /**
   * Колбэк смены города.
   * @param city — выбранный город или null
   */
  onChange: (city: string | null) => void
}

const ColorButton = styled(Button)<ButtonProps>(() => ({
  color: '#6c757d',
  backgroundColor: '#fff',
  '&:hover': {
    backgroundColor: '#ffffff',
  },
}))

/**
 * Фильтр по эффективному городу клиента.
 * @param cities — доступные города
 * @param selectedCity — текущий выбор
 * @param onChange — колбэк
 */
const CityFilter: FC<CityFilterProps> = ({
  cities,
  selectedCity,
  onChange,
}) => {
  const [open, setOpen] = useState(false)
  const anchorRef = useRef<HTMLDivElement>(null)

  const options = useMemo(
    () => [
      { id: '', value: 'Все города' },
      ...cities.map(cityName => ({ id: cityName, value: cityName })),
    ],
    [cities]
  )

  const selectedValue =
    options.find(option => option.id === (selectedCity ?? ''))?.value ??
    'Все города'

  /**
   * Выбирает город из меню.
   * @param cityId — id опции (пусто = все города)
   */
  const handleMenuItemClick = (cityId: string): void => {
    setOpen(false)
    onChange(cityId ? cityId : null)
  }

  const handleToggle = (): void => {
    setOpen(previousOpen => !previousOpen)
  }

  /**
   * Закрывает меню при клике снаружи.
   * @param event — DOM-событие
   */
  const handleClose = (event: Event): void => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return
    }
    setOpen(false)
  }

  return (
    <>
      <ButtonGroup
        variant='contained'
        ref={anchorRef}
        aria-label='city filter'
        sx={{ borderRadius: 28, width: '100%', boxShadow: 3 }}
      >
        <ColorButton
          onClick={handleToggle}
          sx={{ borderRadius: 28, overflow: 'hidden', whiteSpace: 'nowrap' }}
          fullWidth
        >
          {selectedValue}
        </ColorButton>
        <ColorButton
          size='small'
          aria-controls={open ? 'city-filter-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup='menu'
          onClick={handleToggle}
          sx={{ borderRadius: 28 }}
        >
          <ArrowDropDownIcon />
        </ColorButton>
      </ButtonGroup>
      <Popper
        sx={{ zIndex: 1, borderRadius: 28 }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='city-filter-menu' autoFocusItem>
                  {options.map(option => (
                    <MenuItem
                      key={option.id || 'all-cities'}
                      selected={option.id === (selectedCity ?? '')}
                      onClick={() => handleMenuItemClick(option.id)}
                    >
                      {option.value}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}

export default CityFilter
